/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch } from 'react-redux';

import Heading from '../components/heading/Heading';
import Item from '../components/custom/Item';
import CustomNoRowsOlverlay from '../components/custom/CustomNoRowsOlverlay';
import SolicitudesDataGrid from '../components/styledDataGrid/SolicitudesDataGrid';
import msgNotificacion from '../scripts/notification';
import msgAlerta from '../scripts/alert';
import Formatter from '../scripts/formatters';
import FeriadosService from '../services/feriadosService';
import { showProgress } from '../redux/reducers/progress.Slice';
import { PAGE_SIZE, NAME_PROJECT } from '../redux/constants/index';
import { fechaObservanciaFeriadoDesdeKey } from '../utils/feriados';
import { fechaApiToKey, formatearFechaApi } from '../utils/fechas';

const anioActual = () => new Date().getFullYear();

export default function Feriados() {
  const dispatch = useDispatch();
  const userLogin = JSON.parse(localStorage.getItem('user'));
  const esAdminRrhh = [1, 2].includes(Number(userLogin?.CodigoRol));

  const [loading, setLoading] = useState(false);
  const [anioFiltro, setAnioFiltro] = useState(anioActual());
  const [rows, setRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDuplicar, setModalDuplicar] = useState(false);
  const [anioDestino, setAnioDestino] = useState(anioActual() + 1);
  const [form, setForm] = useState({ idFeriado: null, nombre: '', fechaFeriado: '' });

  const observanciaPreview = useMemo(
    () => (form.fechaFeriado ? fechaObservanciaFeriadoDesdeKey(form.fechaFeriado) : null),
    [form.fechaFeriado]
  );

  const cargarFeriados = async () => {
    if (!esAdminRrhh) return;
    setLoading(true);
    try {
      const response = await FeriadosService.getFeriados({ anio: anioFiltro });
      if (response?.Codigo === 1) setRows(response.Data || []);
      else setRows([]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${NAME_PROJECT} | Feriados`;
    if (!esAdminRrhh) {
      msgNotificacion('Solo Admin o RRHH pueden gestionar feriados.', 'warning');
      return;
    }
    cargarFeriados();
  }, [anioFiltro]);

  const abrirModal = (row = null) => {
    if (row) {
      setForm({
        idFeriado: row.IdFeriado,
        nombre: row.Nombre,
        fechaFeriado: fechaApiToKey(row.FechaFeriado) || '',
      });
    } else {
      setForm({ idFeriado: null, nombre: '', fechaFeriado: '' });
    }
    setModalOpen(true);
  };

  const guardar = async () => {
    if (!form.nombre?.trim() || !form.fechaFeriado) {
      msgNotificacion('Complete nombre y fecha del feriado.', 'warning');
      return;
    }
    dispatch(showProgress(true));
    try {
      const response = await FeriadosService.guardarFeriado({
        idFeriado: form.idFeriado,
        nombre: form.nombre.trim(),
        fechaFeriado: fechaApiToKey(form.fechaFeriado) || form.fechaFeriado,
        idUsuario: userLogin.IdUsuario,
        codigoRol: userLogin.CodigoRol,
      });
      if (response?.Codigo === 1) {
        msgNotificacion('Feriado guardado.', 'success');
        setModalOpen(false);
        await cargarFeriados();
      } else {
        msgNotificacion(response?.Mensaje || 'No se pudo guardar.', 'warning');
      }
    } finally {
      dispatch(showProgress(false));
    }
  };

  const eliminar = (row) => {
    msgAlerta(
      'Eliminar feriado',
      `¿Desactivar el feriado "${row.Nombre}"?`,
      'question',
      async () => {
        dispatch(showProgress(true));
        try {
          const response = await FeriadosService.eliminarFeriado({
            idFeriado: row.IdFeriado,
            codigoRol: userLogin.CodigoRol,
          });
          if (response?.Codigo === 1) {
            msgNotificacion('Feriado eliminado.', 'success');
            await cargarFeriados();
          } else {
            msgNotificacion(response?.Mensaje || 'No se pudo eliminar.', 'warning');
          }
        } finally {
          dispatch(showProgress(false));
        }
      }
    );
  };

  const duplicar = async () => {
    dispatch(showProgress(true));
    try {
      const response = await FeriadosService.duplicarFeriadosAnio({
        anioOrigen: anioFiltro,
        anioDestino: Number(anioDestino),
        idUsuario: userLogin.IdUsuario,
        codigoRol: userLogin.CodigoRol,
      });
      if (response?.Codigo === 1) {
        msgNotificacion(response.Mensaje, 'success');
        setModalDuplicar(false);
        setAnioFiltro(Number(anioDestino));
      } else {
        msgNotificacion(response?.Mensaje || 'No se duplicaron feriados.', 'warning');
      }
    } finally {
      dispatch(showProgress(false));
    }
  };

  const columns = [
    { field: 'IdFeriado', headerName: 'ID', width: 70 },
    { field: 'Nombre', headerName: 'Nombre', minWidth: 200, flex: 1 },
    {
      field: 'FechaFeriado',
      headerName: 'Fecha feriado',
      width: 130,
      valueFormatter: (v) => formatearFechaApi(v) || '—',
    },
    {
      field: 'FechaObservancia',
      headerName: 'Fecha observancia',
      width: 150,
      valueFormatter: (v) => formatearFechaApi(v) || '—',
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row">
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => abrirModal(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => eliminar(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (!esAdminRrhh) {
    return (
      <Grid container padding={2}>
        <Grid item xs={12}>
          <Heading title="Feriados" />
          <Typography sx={{ mt: 2 }}>No tiene permisos para gestionar feriados.</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={1} paddingX={1} paddingTop={1}>
      <Grid item xs={12}>
        <Heading title="Feriados" />
      </Grid>
      <Grid item xs={12}>
        <Item>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Año"
              value={anioFiltro}
              onChange={(e) => setAnioFiltro(Number(e.target.value))}
              sx={{ minWidth: 120 }}
            >
              {[anioActual() - 1, anioActual(), anioActual() + 1, anioActual() + 2].map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirModal()}>
              Agregar feriado
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                setAnioDestino(anioFiltro + 1);
                setModalDuplicar(true);
              }}
            >
              Duplicar al año siguiente
            </Button>
            <Typography variant="body2" color="text.secondary">
              La <strong>fecha observancia</strong> es la que cuenta (no laborable). Si el feriado cae domingo, se observa el lunes.
            </Typography>
          </Stack>

          <SolicitudesDataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.IdFeriado}
            autoHeight
            loading={loading}
            pageSize={PAGE_SIZE}
            localeText={Formatter.localizedTextsMapColumns}
            slots={{ noRowsOverlay: CustomNoRowsOlverlay }}
            density="compact"
          />
        </Item>
      </Grid>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.idFeriado ? 'Editar feriado' : 'Nuevo feriado'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nombre"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            />
            <TextField
              fullWidth
              type="date"
              label="Fecha del feriado"
              InputLabelProps={{ shrink: true }}
              value={form.fechaFeriado}
              onChange={(e) => setForm({ ...form, fechaFeriado: e.target.value })}
            />
            {observanciaPreview && (
              <Typography variant="body2" color="text.secondary">
                Se observará el: <strong>{formatearFechaApi(observanciaPreview)}</strong>
                {form.fechaFeriado !== observanciaPreview && ' (feriado cae domingo)'}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalDuplicar} onClose={() => setModalDuplicar(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Duplicar feriados</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Copia los feriados activos de <strong>{anioFiltro}</strong> al año destino y recalcula la fecha observancia.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Año destino"
            value={anioDestino}
            onChange={(e) => setAnioDestino(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalDuplicar(false)}>Cancelar</Button>
          <Button variant="contained" onClick={duplicar}>Duplicar</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
